import styled from "styled-components"

import {
  Banner,
  breakpoint,
  colors,
  Skeleton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { AiAssistantContextMeta } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContext/AiAssistantContextMeta"
import { AiAssistantContextSource } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContext/AiAssistantContextSource"
import { AiAssistantContextTargetSelect } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContextLibrary/AiAssistantContextTargetSelect"
import { ExpandableContextGuidelines } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContextLibrary/ExpandableContextGuidelines"
import { CONTEXT_MAX_CHARACTERS } from "src/components/AiAssistant/AiAssistantKnowledge/aiAssistantKnowledgeConstants"
import { TContextTarget } from "src/components/AiAssistant/aiAssistantTypes"
import { useFetchHomeGroup } from "src/data/homeGroups/queries/homeGroupQueries"
import { THomeGroup } from "src/data/homeGroups/types/homeGroupTypes"
import { THome } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHome } from "src/data/organizations/queries/homeQueries"
import {
  useDeletePropertyContext,
  useFetchPropertyContext,
  usePatchPropertyContext,
} from "src/data/propertyContext/propertyContextQueries"
import { TPropertyContext } from "src/data/propertyContext/propertyContextTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { useSearchParams } from "src/router/useSearchParams"
import { MButton } from "src/ui/Button/MButton"
import { Divider } from "src/ui/Divider/Divider"
import { Editable } from "src/ui/Editable/Editable"
import { EditableTextarea } from "src/ui/EditableTextarea/EditableTextarea"
import { BreadcrumbView } from "src/ui/Layout/BreadcrumbView"
import { MCard } from "src/ui/MCard/MCard"
import { MMarkdown } from "src/ui/MMarkdown/MMarkdown"

export function AiAssistantContext({ contextId }: { contextId: string }) {
  const { tPlaceholder, t, langKeys } = useTranslate()

  const { org } = useOrganization()
  const { navigate } = useRouter()

  const { searchParams } = useSearchParams({
    keys: [
      {
        key: "suggestion_id",
        type: "string",
      },
    ],
  })

  const suggestionId = searchParams.suggestion_id
  const hasSuggestion = !!suggestionId

  const fetchPropertyContext = useFetchPropertyContext({
    orgId: org.id,
    contextId,
  })

  const fetchSuggestion = useFetchPropertyContext({
    orgId: org.id,
    contextId: suggestionId ?? "",
    options: {
      enabled: hasSuggestion,
    },
  })

  const fetchHome = useFetchHome({
    orgId: org.id,
    homeId: fetchPropertyContext.data?.home_id ?? "",
    options: {
      enabled: !!fetchPropertyContext.data?.home_id,
      keepPreviousData: false,
    },
  })

  const fetchHomeGroup = useFetchHomeGroup({
    orgId: org.id,
    homeGroupId: fetchPropertyContext.data?.homegroup_id ?? "",
    options: {
      enabled: !!fetchPropertyContext.data?.homegroup_id,
      keepPreviousData: false,
    },
  })

  const patchPropertyContext = usePatchPropertyContext()

  const deletePropertyContextMutation = useDeletePropertyContext()

  function updatePropertyContext(
    updates: Partial<TPropertyContext>,
    onSuccess?: () => void
  ) {
    patchPropertyContext.mutate(
      {
        path: {
          organization_id: org.id,
          property_context_id: contextId,
        },
        body: updates,
      },
      {
        onSuccess,
      }
    )
  }

  function updatePropertyContextTarget(target: TContextTarget | null) {
    if (!target) {
      updatePropertyContext({ home_id: "", homegroup_id: "" })
      return
    }

    if (target.type === "home") {
      updatePropertyContext({ home_id: target.id })
    }

    if (target.type === "homegroup") {
      updatePropertyContext({ homegroup_id: target.id })
    }
  }

  function deletePropertyContext() {
    deletePropertyContextMutation.mutate(
      {
        path: {
          organization_id: org.id,
          property_context_id: contextId,
        },
      },
      {
        onSuccess: () => {
          navigate(Routes.AiAssistant.location())
        },
      }
    )
  }

  if (fetchPropertyContext.isLoading || fetchSuggestion.isInitialLoading) {
    return (
      <div>
        <Skeleton width="30ch" />
        <Skeleton width="40ch" />
        <Skeleton width="40ch" />
      </div>
    )
  }

  if (
    fetchPropertyContext.isError ||
    fetchHome.isError ||
    fetchHomeGroup.isError
  ) {
    return (
      <div>
        <Banner type="error" fullWidth>
          {t(langKeys.failed_contact_support)}
        </Banner>
      </div>
    )
  }

  const contextTarget = getContextTarget(fetchHome.data, fetchHomeGroup.data)

  return (
    <BreadcrumbView
      breadcrumbs={[
        {
          label: tPlaceholder("Knowledge"),
          to: Routes.AiAssistant.location(),
        },
        {
          label: fetchPropertyContext.data.title,
          to: Routes.AiAssistantKnowledgeContent.location({
            contextId,
            suggestionId,
          }),
        },
      ]}
      title={
        <Editable onSave={(title) => updatePropertyContext({ title })}>
          {fetchPropertyContext.data.title}
        </Editable>
      }
    >
      <Main>
        <ContentWrapper>
          {fetchSuggestion.data && (
            <MCard border boxShadow={false}>
              <Text variant="subtitle">
                {tPlaceholder("Suggestion")}: {fetchSuggestion.data.title}
              </Text>

              <Text color="secondary">
                <MMarkdown>{fetchSuggestion.data.content}</MMarkdown>
              </Text>
            </MCard>
          )}

          <EditableTextarea
            value={fetchPropertyContext.data.content}
            onSave={(content) => updatePropertyContext({ content })}
            placeholder={tPlaceholder("Edit content")}
            tooltip={tPlaceholder("Edit content")}
            maxLength={CONTEXT_MAX_CHARACTERS}
            showTextCount
          />
        </ContentWrapper>

        <div>
          <AiAssistantContextMeta context={fetchPropertyContext.data} />

          <Divider $margin={spacing.L} />

          <AiAssistantContextSource />

          <Divider $margin={spacing.L} />

          <BottomWrapper>
            <div>
              <Text
                variant="subtitleS"
                color="secondary"
                marginBottom={spacing.XS}
              >
                {tPlaceholder("Apply to a specific property? Add it below.")}
              </Text>
              <AiAssistantContextTargetSelect
                selectedTarget={contextTarget}
                onChangeTarget={updatePropertyContextTarget}
              />
            </div>

            <MButton
              onClick={deletePropertyContext}
              loading={deletePropertyContextMutation.isPending}
              variant="subtle"
              color="destructive"
              size="small"
              fullWidth
            >
              {tPlaceholder("Delete content")}
            </MButton>

            <ExpandableContextGuidelines />
          </BottomWrapper>
        </div>
      </Main>
    </BreadcrumbView>
  )
}

function getContextTarget(
  contextHome: THome | null | undefined,
  contextHomeGroup: THomeGroup | null | undefined
): TContextTarget | null {
  if (contextHome) {
    return {
      type: "home",
      id: contextHome.home_id,
      name: contextHome.name ?? "",
    }
  }

  if (contextHomeGroup) {
    return {
      type: "homegroup",
      id: contextHomeGroup.id,
      name: contextHomeGroup.name,
    }
  }

  return null
}

const Main = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;

  & > div:first-child {
    border-bottom: 1px solid ${colors.divider};
    padding-bottom: ${spacing.M};
    margin-bottom: ${spacing.M};
  }

  @container (${breakpoint.normalUp}) {
    grid-template-columns: 2fr 1fr;

    & > div:first-child {
      border-bottom: none;
      padding-bottom: 0;
      margin-bottom: 0;

      border-right: 1px solid ${colors.divider};
      padding-right: ${spacing.M};
      margin-right: ${spacing.M};
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
`

const BottomWrapper = styled.div`
  display: grid;
  gap: ${spacing.L};
`
